import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconSvg, Text } from '@/lib/v2/components';

import { atomRuleData } from '@/modules/RulesModule/atoms/rules';
import {
  TRANSLATE_RULES_LABELS,
  TRANSLATE_RULES_SEQUENCE_SETTINGS,
  TRANSLATE_RULES_TAG_TYPE_OPTIONS,
  TRANSLATE_RULES_TRIGGER_TYPES,
} from '@/modules/RulesModule/constants';
import { RulesDateIcon } from '@/modules/RulesModule/images/icons';
import { RulesOptions } from '@/modules/RulesModule/interfaces';
import { capitalizeFirstLetter } from '@/modules/RulesModule/utils/capitalizeFirstLetter';

const NodeContentTag = () => {
  const { t } = useTranslation('rules');
  const rulesData = useAtomValue(atomRuleData);

  const formatTriggerDetails = useCallback(
    (formValues: RulesOptions): string => {
      const { frequency, hours, minutes } = formValues;

      const formattedString = [
        frequency?.name,
        `${t(`${TRANSLATE_RULES_SEQUENCE_SETTINGS}.atTime`)} ${hours?.name ?? '00'}:${
          minutes?.name ?? '00'
        }`,
      ].join(' ');

      return capitalizeFirstLetter(formattedString);
    },
    [t]
  );

  const tagTitle = useMemo(() => {
    return rulesData.tagsGroupField?.length === 0
      ? t(`${TRANSLATE_RULES_TAG_TYPE_OPTIONS}.anyTag`)
      : t(`${TRANSLATE_RULES_LABELS}.tagGroups`);
  }, [rulesData.tagsGroupField?.length, t]);

  return (
    <div className="flex flex-col justify-center">
      <div className="mb-2 flex items-center">
        <IconSvg fillColor="primary" height="24px" svgComponent={<RulesDateIcon />} width="24px" />
        <Text className="ml-2 mt-1" fontWeight="semibold" variant="text">
          {t(`${TRANSLATE_RULES_TRIGGER_TYPES}.${rulesData.trigger?.id ?? ''}`)}
        </Text>
      </div>
      <hr className="w-full min-w-64" />
      <div className="flex flex-col justify-center pb-4 pt-2">
        <Text fontWeight="medium" variant="text">
          {tagTitle}
        </Text>
        <Text truncate fontWeight="medium" variant="text-sm">
          {rulesData && formatTriggerDetails(rulesData)}
        </Text>
      </div>
    </div>
  );
};

export default NodeContentTag;
